import { useCallback, useEffect, useState } from 'react';
import { jornadasDeTrabajador, pdvsConDinamica } from '../api/canjes';
import { resumirDesempeno, type JornadaDesempeno } from '../domain/desempeno';

/** Primer y último día del mes. */
function rangoDelMes(mes: string): { desde: string; hasta: string } {
  const [a, m] = mes.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

interface Fila extends JornadaDesempeno {
  pdvName: string | null;
  campaign: string;
}

/**
 * Desempeño de un trabajador en un mes: sus jornadas y lo que registró en cada una.
 *
 * Pedido del usuario: *"luego vamos a tener una vista por trabajador y ver cómo ha sido también
 * su performance, usando también KPIs como estos, porque esto va de la mano con su performance
 * de impulso de venta, entonces es medible"*.
 *
 * LO QUE MÁS IMPORTA DE ESTA PANTALLA ES EL DENOMINADOR. El promedio se calcula solo sobre las
 * jornadas donde de verdad corría una dinámica y la persona asistió. Dividir entre todas
 * castigaría a quien pasó semanas en tiendas sin promoción —donde no hay nada que registrar— y
 * lo haría parecer flojo por una decisión de programación que no tomó él.
 */
export default function DesempenoTrabajador({ workerId, nombre }: {
  workerId: string;
  nombre: string;
}) {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [filas, setFilas] = useState<Fila[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { desde, hasta } = rangoDelMes(mes);
      const jornadas = await jornadasDeTrabajador(workerId, desde, hasta);
      // La vigencia se consulta por FECHA, así que se pide una vez por día distinto.
      const dias = [...new Set(jornadas.map((j) => j.fecha))];
      const vigencias = new Map<string, Set<string>>();
      await Promise.all(dias.map(async (d) => { vigencias.set(d, await pdvsConDinamica(d)); }));
      setFilas(jornadas.map((j) => ({
        ...j,
        conDinamica: j.pdvId ? (vigencias.get(j.fecha)?.has(j.pdvId) ?? false) : false,
      })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el desempeño.');
    } finally {
      setCargando(false);
    }
  }, [workerId, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  const r = resumirDesempeno(filas);

  return (
    <section className="rounded border bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Desempeño de {nombre}</h2>
        <input
          type="month"
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>

      {error && <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {cargando && <p className="text-sm text-gray-500">Cargando…</p>}

      {!cargando && (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded border p-2">
              <p className="text-xl font-bold">{r.asistidas}/{r.jornadas}</p>
              <p className="text-xs text-gray-600">jornadas asistidas</p>
            </div>
            <div className="rounded border p-2">
              <p className="text-xl font-bold">{r.canjes}</p>
              <p className="text-xs text-gray-600">canjes registrados</p>
            </div>
            <div className="rounded border p-2">
              <p className="text-xl font-bold">{r.promedio ?? '—'}</p>
              <p className="text-xs text-gray-600">
                por jornada con dinámica{r.jornadasConDinamica > 0 ? ` (${r.jornadasConDinamica})` : ''}
              </p>
            </div>
            <div className={`rounded border p-2 ${r.enCero > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
              <p className="text-xl font-bold">{r.enCero}</p>
              <p className="text-xs text-gray-600">días con dinámica sin registrar nada</p>
            </div>
          </div>

          {/* Se dice CÓMO se calcula, en la pantalla y no en una ayuda: es un número con el que
              se va a evaluar a una persona, y tiene derecho a saber contra qué se lo mide. */}
          <p className="mb-3 text-xs text-gray-500">
            El promedio cuenta solo las jornadas donde corría una dinámica y asistió. Las tiendas
            sin promoción no bajan su número: ahí no hay nada que registrar.
          </p>

          {filas.length === 0 ? (
            <p className="text-sm text-gray-500">Sin jornadas aprobadas en este mes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Tienda</th>
                    <th className="p-2 text-left">Campaña</th>
                    <th className="p-2">Asistió</th>
                    <th className="p-2">Canjes</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={`${f.fecha}-${i}`} className="border-t">
                      <td className="p-2">{f.fecha.slice(8)}/{f.fecha.slice(5, 7)}</td>
                      <td className="p-2">{f.pdvName ?? '—'}</td>
                      <td className="p-2">{f.campaign}</td>
                      <td className="p-2 text-center">
                        {f.asistio
                          ? <span className="text-green-700">sí</span>
                          : <span className="text-red-700">no</span>}
                      </td>
                      <td className="p-2 text-center">
                        {!f.conDinamica
                          ? <span className="text-gray-300" title="Ese día no corría ninguna dinámica en esa tienda">—</span>
                          : f.canjes === 0
                            ? <span className="text-amber-700">0</span>
                            : <span className="font-semibold">{f.canjes}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
